import type { ReactNode } from 'react';

import focus from '../primitives/focus.module.css';
import styles from './AppBar.module.css';

export interface AppBarProps {
  readonly title: string;
  /**
   * A second line under the title, in the muted ink. `LED-01`'s greeting lives here.
   *
   * It is not a heading — a screen reader jumping by heading should land on the title and hear the screen,
   * not on "Good evening, Asha" and hear the time of day.
   */
  readonly subtitle?: string;
  /** Present shows a back control. Its accessible name says where back goes, not just "Back". */
  readonly onBack?: () => void;
  readonly backLabel?: string;
  /**
   * What the leading control means. `back` is `←` — return to where you came from. `dismiss` is `✕` —
   * throw this away (`FEAT-011 §2.1`). The screen book draws them differently on `LED-04` and `LED-02`
   * because they are different promises, and a person half-way through a form deserves to know which one
   * they are pressing.
   */
  readonly backKind?: 'back' | 'dismiss';
  /** One action, on the right. More than one belongs in the screen, not the bar. */
  readonly action?: ReactNode;
}

/**
 * The top bar. The title is a real heading, so a screen reader can jump to it, and the back control is a
 * button rather than an icon with a click handler.
 *
 * When there is a back control and no action, an empty box of the same width keeps the title optically
 * centred without a magic margin.
 */
export function AppBar({
  title,
  subtitle,
  onBack,
  backLabel,
  backKind = 'back',
  action,
}: AppBarProps): React.JSX.Element {
  return (
    <header className={styles.bar}>
      {onBack ? (
        <button
          type="button"
          className={[styles.icon, focus.focusable].join(' ')}
          onClick={onBack}
          aria-label={backLabel ?? (backKind === 'dismiss' ? 'Close' : 'Back')}
        >
          <span aria-hidden="true">{backKind === 'dismiss' ? '✕' : '←'}</span>
        </button>
      ) : null}
      <div className={[styles.titles, onBack ? styles.centred : ''].filter(Boolean).join(' ')}>
        <h1 className={styles.title}>{title}</h1>
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      </div>
      {action ?? (onBack ? <span className={styles.spacer} /> : null)}
    </header>
  );
}
